import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { 
  FolderOpen, 
  Plus, 
  Search, 
  Edit,
  Trash2,
  FolderPlus,
  FileText,
  Users,
  Grid3x3,
  Eye,
  X,
  UserPlus
} from 'lucide-react';
import IOSModal from '../../ui/IOSModal';
import CreateGroupModal from './CreateGroupModal';
import EditGroupModal from './EditGroupModal';
import GroupDetailsModal from './GroupDetailsModal';
import AssignGroupModal from './AssignGroupModal';

export default function GroupsManager() {
  const { getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const api = getAuthenticatedApi();

  // Stati
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Modali
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAssignGroupModal, setShowAssignGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });

  // Carica dati
  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoading(true);
      const response = await api.get('/gym_template_groups.php?action=list');
      
      // L'API restituisce direttamente l'array di gruppi
      if (Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        console.error('Formato risposta gruppi non valido:', response.data);
        setGroups([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento gruppi:', error);
      toast.error('Errore nel caricamento dei gruppi');
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleGroupCreated = () => {
    setShowCreateModal(false);
    fetchGroups();
    toast.success('Gruppo creato con successo!');
  };

  const handleGroupUpdated = () => {
    setShowEditModal(false);
    setSelectedGroup(null);
    fetchGroups();
    toast.success('Gruppo aggiornato con successo!');
  };

  const handleEdit = (group) => {
    setSelectedGroup(group);
    setShowEditModal(true);
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il gruppo "${group.name}"? Questa azione rimuoverà anche tutte le associazioni con i template.`)) {
      return;
    }

    try {
      const response = await api.delete(`/gym_template_groups.php?action=delete&id=${group.id}`);
      if (response.data.success) {
        fetchGroups();
        toast.success('Gruppo eliminato con successo!');
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del gruppo');
    }
  };

  const handleViewDetails = async (group) => {
    try {
      // Carica dettagli completi con template
      const response = await api.get(`/gym_template_groups.php?action=get&id=${group.id}`);
      
      if (response.data && response.data.id) {
        setSelectedGroup(response.data);
        setShowDetailsModal(true);
      } else {
        console.error('Formato risposta API non valido:', response.data);
        toast.error('Errore nel caricamento dei dettagli del gruppo');
      }
    } catch (error) {
      console.error('Errore nel caricamento dettagli:', error);
      toast.error('Errore nel caricamento dei dettagli del gruppo');
    }
  };

  const handleAssignGroup = async (group) => {
    try {
      // Carica dettagli completi con template per verificare che ci siano template
      const response = await api.get(`/gym_template_groups.php?action=get&id=${group.id}`);
      
      if (response.data && response.data.id) {
        const groupDetails = response.data;
        if (!groupDetails.templates || groupDetails.templates.length === 0) {
          toast.error('Il gruppo non contiene template da assegnare');
          return;
        }
        setSelectedGroup(groupDetails);
        setShowAssignGroupModal(true);
      }
    } catch (error) {
      console.error('Errore nel caricamento dettagli gruppo:', error);
      toast.error('Errore nel caricamento dei dettagli del gruppo');
    }
  };

  // Filtra gruppi
  const filteredGroups = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         (group.description && group.description.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              📁 Gruppi Template
            </h1>
            <p className="text-gray-600 dark:text-white/65 mt-1">
              Organizza i template in gruppi per una migliore gestione
            </p>
          </div>
          
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setModalOrigin({ 
                x: rect.left + rect.width / 2, 
                y: rect.top + rect.height / 2 
              });
              setShowCreateModal(true);
            }}
            className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nuovo Gruppo
          </button>
        </div>

        {/* Statistiche rapide */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<FolderOpen className="h-5 w-5" />}
            label="Gruppi Totali"
            value={groups.length}
            color="blue"
          />
          <StatCard
            icon={<FileText className="h-5 w-5" />}
            label="Gruppi con Template"
            value={groups.filter(g => g.template_count > 0).length}
            color="green"
          />
          <StatCard
            icon={<Grid3x3 className="h-5 w-5" />}
            label="Template nei Gruppi"
            value={groups.reduce((sum, g) => sum + parseInt(g.template_count || 0), 0)}
            color="purple"
          />
          <StatCard
            icon={<FolderPlus className="h-5 w-5" />}
            label="Gruppi Attivi"
            value={groups.filter(g => g.is_active).length}
            color="yellow"
          />
        </div>

        {/* Filtri */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Cerca gruppi..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Lista Gruppi */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600 dark:text-white/65">Caricamento gruppi...</span>
        </div>
      ) : filteredGroups.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map(group => (
            <GroupCard
              key={group.id}
              group={group}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onViewDetails={handleViewDetails}
              onAssignGroup={handleAssignGroup}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <FolderOpen className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchQuery
              ? 'Nessun gruppo trovato'
              : 'Nessun gruppo disponibile'}
          </h3>
          <p className="text-gray-500 dark:text-white/65 mb-6">
            {searchQuery
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea il primo gruppo per organizzare i template'}
          </p>
          {!searchQuery && (
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setModalOrigin({ 
                  x: rect.left + rect.width / 2, 
                  y: rect.top + rect.height / 2 
                });
                setShowCreateModal(true);
              }}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5 mr-2 inline" />
              Crea Primo Gruppo
            </button>
          )}
        </div>
      )}

      {/* Modali */}
      <IOSModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        origin={modalOrigin}
        size="medium"
        title="Nuovo Gruppo"
      >
        {showCreateModal && (
          <CreateGroupModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleGroupCreated}
          />
        )}
      </IOSModal>

      {showEditModal && selectedGroup && (
        <EditGroupModal
          isOpen={showEditModal}
          group={selectedGroup}
          onClose={() => {
            setShowEditModal(false);
            setSelectedGroup(null);
          }}
          onSuccess={handleGroupUpdated}
        />
      )}

      {showDetailsModal && selectedGroup && (
        <GroupDetailsModal
          isOpen={showDetailsModal}
          group={selectedGroup}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedGroup(null);
          }}
          onUpdate={fetchGroups}
        />
      )}

      {showAssignGroupModal && selectedGroup && (
        <AssignGroupModal
          isOpen={showAssignGroupModal}
          group={selectedGroup}
          onClose={() => {
            setShowAssignGroupModal(false);
            setSelectedGroup(null);
          }}
          onSuccess={() => {
            fetchGroups();
            toast.success('Gruppo assegnato con successo!');
          }}
        />
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE: Card Gruppo
// =====================================================
function GroupCard({ group, onEdit, onDelete, onViewDetails, onAssignGroup }) {
  const { isDark } = useTheme();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {group.name}
              </h3>
              {!group.is_active && (
                <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-white/65">
                  Inattivo
                </span>
              )}
            </div>
            {group.description && group.description.trim() !== '' && (
              <p className="text-sm text-gray-600 dark:text-white/65 line-clamp-2">
                {group.description}
              </p>
            )}
          </div>
        </div>

        {/* Badge colore */}
        {group.color && (
          <div 
            className="inline-block w-6 h-6 rounded-full border-2 border-gray-300 dark:border-gray-700"
            style={{ backgroundColor: group.color }}
            title={`Colore: ${group.color}`}
          />
        )}
      </div>

      {/* Info */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-white/65 mb-3">
          <span className="flex items-center">
            <FileText className="h-4 w-4 mr-1" />
            {group.template_count || 0} template
          </span>
          {group.created_by_name && (
            <span className="text-xs">
              da {group.created_by_name}
            </span>
          )}
        </div>

        {/* Azioni */}
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => onViewDetails(group)}
            className="flex items-center justify-center px-2 py-2 text-sm text-gray-700 dark:text-white/85 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:brightness-110 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
          >
            <Eye className="h-4 w-4 mr-1" />
            Dettagli
          </button>
          
          <button
            onClick={() => onAssignGroup && onAssignGroup(group)}
            className="flex items-center justify-center px-2 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
            title="Assegna tutti i template del gruppo"
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Assegna
          </button>
          
          <button
            onClick={() => onEdit(group)}
            className="flex items-center justify-center px-2 py-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <Edit className="h-4 w-4 mr-1" />
            Modifica
          </button>
          
          <button
            onClick={() => onDelete(group)}
            className="flex items-center justify-center px-2 py-2 text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// COMPONENTE: Stat Card
// =====================================================
function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg mr-3 ${colors[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-white/65">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}

